export interface IPlatformInfo {
  os: string;
  isMobile: boolean;
}

export interface IBrowserInfo {
  name: string;
  version: string;
  userAgent: string;
}

export interface IMobileInfo {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isTouchDevice: boolean;
}

export interface IDeviceInfo {
  platform: IPlatformInfo;
  browser: IBrowserInfo;
}

export class DeviceUtils {
  public static isMobile(): boolean {
    return DeviceUtils.getMobileInfo().isMobile;
  }

  public static getMobileInfo(): IMobileInfo {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    return {
      isMobile,
      isIOS: /iPhone|iPad|iPod/i.test(navigator.userAgent),
      isAndroid: /Android/i.test(navigator.userAgent),
      isTouchDevice: 'ontouchstart' in window || navigator.maxTouchPoints > 0
    };
  }

  public static getBrowserInfo(): IBrowserInfo {
    const ua = navigator.userAgent;
    let browserName = 'Unknown';

    if (ua.match(/chrome|chromium|crios/i)) {
      browserName = 'Chrome';
    }
    else if (ua.match(/firefox|fxios/i)) {
      browserName = 'Firefox';
    }
    else if (ua.match(/safari/i)) {
      browserName = 'Safari';
    }
    else if (ua.match(/opr\//i)) {
      browserName = 'Opera';
    }
    else if (ua.match(/edg/i)) {
      browserName = 'Edge';
    }

    return {
      name: browserName,
      version: DeviceUtils.getBrowserVersion(browserName, ua),
      userAgent: ua
    };
  }

  public static getPlatformInfo(): IPlatformInfo {
    // Try modern UA Client Hints API first
    if ('userAgentData' in navigator) {
      const uaData = navigator.userAgentData as any;
      return {
        os: uaData.platform,
        isMobile: uaData.mobile
      };
    }

    // Fall back to userAgent parsing
    const ua = navigator.userAgent;
    let os = 'Unknown';

    if (/Windows/i.test(ua)) {
      os = 'Windows';
    }
    else if (/Macintosh|Mac OS X/i.test(ua)) {
      os = 'macOS';
    }
    else if (/Linux/i.test(ua)) {
      os = 'Linux';
    }
    else if (/Android/i.test(ua)) {
      os = 'Android';
    }
    else if (/iPhone|iPad|iPod/i.test(ua)) {
      os = 'iOS';
    }

    return {
      os,
      isMobile: DeviceUtils.getMobileInfo().isMobile
    };
  }

  public static getBrowserVersion(browserName: string, userAgent: string): string {
    let version = 'Unknown';

    if (browserName === 'Chrome') {
      const match = userAgent.match(/(?:Chrome|Chromium|Crios)\/([0-9.]+)/);
      version = match ? match[1] : version;
    }
    else if (browserName === 'Firefox') {
      const match = userAgent.match(/(?:Firefox|FxiOS)\/([0-9.]+)/);
      version = match ? match[1] : version;
    }
    else if (browserName === 'Safari') {
      const match = userAgent.match(/Version\/([0-9.]+)/);
      version = match ? match[1] : version;
    }
    else if (browserName === 'Edge') {
      const match = userAgent.match(/Edg\/([0-9.]+)/);
      version = match ? match[1] : version;
    }

    return version;
  }

  public static getDeviceInfo(): IDeviceInfo {
    const browserInfo = DeviceUtils.getBrowserInfo();
    const platformInfo = DeviceUtils.getPlatformInfo();

    return {
      platform: platformInfo,
      browser: browserInfo
    };
  }
}
